import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { DescriptionList } from '@/components/ui/DescriptionList'
import { PageHeader } from '@/components/ui/PageHeader'
import { staffPortalQuery } from '@/features/portal/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDate, humanize } from '@/utils/format'

export function StaffProfilePage() {
  const { t } = useTranslation('portal')
  useDocumentTitle('My profile')
  const { data, isPending, error, refetch } = useQuery(staffPortalQuery())
  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />
  const p = data.profile

  return (
    <>
      <PageHeader
        title={t('My profile')}
        description={t('Your record as held by the school office. Ask the office to correct anything.')}
        breadcrumbs={[{ label: t('Profile') }]}
        homeHref="/staff"
      />
      <div className="grid gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <Card>
          <CardBody className="flex flex-col items-center text-center">
            <Avatar name={p.fullName} src={p.photoUrl} size="xl" />
            <p className="mt-4 text-lg font-semibold text-ink">{p.fullName}</p>
            <p className="text-sm text-muted">{p.designation?.name ?? t('Staff')}</p>
            <p className="mt-2 font-mono text-xs text-subtle">{p.employeeCode}</p>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t('Details')} />
          <CardBody>
            <DescriptionList
              items={[
                { label: t('Department'), value: p.department?.name ?? '—' },
                { label: t('Main subject'), value: p.primarySubject?.name ?? '—' },
                { label: t('Employment type'), value: humanize(p.employmentType) },
                { label: t('Joined'), value: formatDate(p.joiningDate) },
                { label: t('Email'), value: p.email ?? '—' },
                { label: t('Phone'), value: p.phone ?? '—' },
                { label: t('Qualification'), value: p.qualification ?? '—' },
                { label: t('Blood group'), value: p.bloodGroup ?? '—' },
              ]}
            />
          </CardBody>
        </Card>
      </div>
    </>
  )
}
